 

import { Implementation } from '@/backend/models/implementation';
import { ImplementationCreateData, ImplementationParams, ImplementationReadAllResponse, ImplementationReply } from '@/backend/access/implementation/types';
import { create } from 'zustand';
import { ImplementationController } from '../controllers/implementationController';
import { FusaReply } from '@/types/replyTypes';
import { useGlobalStore } from './GlobalStore';
import { FusaToastType } from '../toasts/types';

type ImplementationStore = {
  implementations: Implementation[];
  implementation: Implementation | null;

  setImplementations: (implementations: ImplementationParams[]) => void;

  createImplementation: (request: Omit<ImplementationCreateData, 'orgId'>) => Promise<ImplementationReply>;
  createImplementationFromInterface: (interfaceId: string) => Promise<ImplementationReply>;
  updateImplementation: (implementation: ImplementationParams) => Promise<ImplementationReply>;
  deleteImplementation: (id: string) => Promise<FusaReply>;
  fetchImplementation: (id: string) => Promise<ImplementationReply>;
  fetchAllImplementations: () => Promise<ImplementationReadAllResponse>;
  fetchAllMatchingFunctionalInterface: (functionalInterfaceId: string) => Promise<ImplementationReadAllResponse>;

  getImplementationById: (id: string) => Implementation | undefined;
  reset: () => void;
};

const initialState = {
  implementations: [] as Implementation[],
  implementation: null,
};

export const useImplementationStore = create<ImplementationStore>()((set, get) => ({
  ...initialState,

  setImplementations: (implementations) => {
    const builtImplementations = implementations.map((implementation) => new Implementation(implementation));
    set({ implementations: builtImplementations });
  },

  createImplementation: async (request: Omit<ImplementationCreateData, 'orgId'>) => {
    const controller = new ImplementationController();
    const response = await controller.createImplementation(request);
    if (!response.errors && response.implementation) {
      get().setImplementations([...get().implementations, response.implementation]);
    }
    return response;
  },

  createImplementationFromInterface: async (interfaceId: string) => {
    const response = await new ImplementationController().createFromInterface(interfaceId);
    if (!response.errors && response.implementation) {
      useGlobalStore.getState().addToast({ 
        message: 'Implementation created successfully', 
        header: 'Success', 
        type: FusaToastType.SUCCESS, 
        link: `/admin/implementations/${response.implementation.id}`, 
        linkText: 'View Implementation' 
      });
    }
    return response;
  },

  updateImplementation: async (implementation: ImplementationParams) => {
    const controller = new ImplementationController();
    const response = await controller.updateImplementation(implementation);
    if (!response.errors && response.implementation) {
      const updatedImplementations = get().implementations.map((implementation) => implementation.id === response.implementation?.id ? response.implementation : implementation);
      get().setImplementations(updatedImplementations);
    }
    return response;
  },

  deleteImplementation: async (id: string) => {
    const controller = new ImplementationController();
    const response = await controller.deleteImplementation(id);
    if (!response.errors && response.status === 200) {
      get().setImplementations(get().implementations.filter((implementation) => implementation.id !== id));
    }
    return response;
  },

  fetchImplementation: async (id: string) => {
    const controller = new ImplementationController();
    const response = await controller.readImplementation(id);
    if (!response.errors && response.implementation) {
      set({ implementation: new Implementation(response.implementation)});
    }
    return response;
  },

  fetchAllImplementations: async () => {
    const controller = new ImplementationController();
    const response = await controller.readAllImplementations();
    if (!response.errors && response.implementations) {
      set({ implementations: response.implementations.map((implementation) => new Implementation(implementation)) });
    }
    return response;
  },
  fetchAllMatchingFunctionalInterface: async (functionalInterfaceId: string) => {
    const response = await new ImplementationController().readAllMatchingFunctionalInterface(functionalInterfaceId);
    if (!response.errors && response.implementations) {
      set({ implementations: response.implementations.map((implementation) => new Implementation(implementation)) });
    }
    return response;
  },
  getImplementationById: (id) => {
    const { implementations } = get();
    return implementations.find(implementation => implementation.id === id);
  },

  reset: () => {
    set(initialState);
  },
}));
